import React from 'react'
import { Box, Button, Stack, Typography } from '@mui/material'
import Image from 'next/image'
import Link from 'next/link'

import { TooltipCustom } from '#/shared'
import styles from '#/shared/styles/api-keys.module.scss'

import { ApiKeysPageState } from '../model/use-api-keys'

import { CopyButton, KeyItem, KeyItemDetail, KeyItemLimitField } from './components/key-item'

type ApiKeysMobileProps = {
	state: ApiKeysPageState
}

export const ApiKeysMobile: React.FC<ApiKeysMobileProps> = ({ state }) => {
	const { keyItems, hasKeys, disabled, setModalOpen, deleteKey } = state

	return (
		<Stack spacing={2} className={styles.wrap}>
			<Stack spacing={2} className='pd-30 bg-color-block border-radius-main'>
				<Typography className='text'>
					API-ключ — это инструмент, который идентифицирует пользователя или программу,
					запрашивающих доступ к API платформы. С помощью ключа можно отслеживать, кто и
					когда пользуется API, рассчитывать оплату.
				</Typography>
				<Box display='flex' alignItems='center'>
					<Button
						onClick={() => setModalOpen(true)}
						disabled={disabled}
						className='btn-classic'
						sx={{ width: '130px' }}
					>
						Создать ключ
					</Button>
					<Link href='https://air-docs.readthedocs.io/' target='_blank'>
						<Typography
							className='text'
							sx={{
								marginLeft: '15px',
								color: '#8280FF !important',
							}}
						>
							Документация
						</Typography>
					</Link>
				</Box>
			</Stack>

			{hasKeys ? (
				<Box
					className='border-radius-main'>
					<Stack spacing={1.5}>
						{keyItems.map((item, index) => (
							<Box key={item.id}>
								<KeyCard {...item} onDelete={deleteKey} />
								{index !== keyItems.length - 1 && (
									<Box
										sx={{
											height: 1,
											backgroundImage:
												'linear-gradient(90deg, rgba(255,255,255,0) 0%, rgba(130,128,255,0.4) 50%, rgba(255,255,255,0) 100%)',
											opacity: 0.6,
										}}
									/>
								)}
							</Box>
						))}
					</Stack>
				</Box>
			) : (
				<EmptyState />
			)}
		</Stack>
	)
}

type KeyCardProps = {
	id: string
	name: string
	keyValue: string
	limit: string | number | null
	createdAt: string
	expiresAt: string | null
	onDelete: (name: string) => void | Promise<void>
}

const KeyCard: React.FC<KeyCardProps> = ({ id, name, keyValue, limit, createdAt, expiresAt, onDelete }) => (
	<KeyItem keyValue={keyValue} name={name} limit={limit} createdAt={createdAt} expiresAt={expiresAt} onDelete={onDelete}>
		{({ isCopy, handleCopy, handleLimitChange, limit: currentLimit }) => (
			<Stack key={id} spacing={2} className='pd-30 bg-color-block border-radius-main'>
				<KeyItemDetail title='Имя' value={name} />

				<Stack spacing={1}>
					<Typography className='title-block'>Ключ</Typography>
					<Stack direction='row' spacing={2} alignItems='center'>
						<Typography
							sx={{
								color: '#8280FF !important',
								wordBreak: 'break-all',
								flex: 1,
							}}
						>
							{keyValue}
						</Typography>
						<CopyButton isCopy={isCopy} onCopy={() => handleCopy(keyValue)} />
					</Stack>
				</Stack>

				<Stack spacing={1}>
					<Typography className='title-block'>Лимит токенов</Typography>
					<KeyItemLimitField value={currentLimit} onChange={handleLimitChange} fullWidth />
				</Stack>

				<KeyItemDetail title='Создан' value={createdAt} />
				<KeyItemDetail title='Действителен до' value={expiresAt ?? 'Бессрочно'} />

				<Stack direction='row' justifyContent='flex-end'>
					<TooltipCustom title={'Удалить ключ'}>
						<Image
							onClick={() => onDelete(name)}
							className={styles.clickableIcon}
							src='/svg/main_menu/trash.svg'
							width={20}
							height={20}
							alt='Удалить'
						/>
					</TooltipCustom>
				</Stack>
			</Stack>
		)}
	</KeyItem>
)

const EmptyState = () => (
	<Box height={140} width={'100%'} display='flex' alignItems='center' justifyContent='center'>
		<Box textAlign='center'>
			<Typography className='title-vspomogatel'>У вас пока нет ключей 😞</Typography>
			<Typography className='title-main-gray'>Создайте первый ключ</Typography>
		</Box>
	</Box>
)

